import { useHistory, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import Container from '~/components/container';
import * as St from '../../styles';
import ContainerFlex from '~/components/containerFlex';
import Grid from '~/components/grid';
import { icons } from '~/assets';
import Input from '~/components/input';
import useApi, { ApiMethod } from '~/hooks/useApi';
import Endpoints from '~/services/endpoints';
import Loading from '~/components/loading';
import Button from '~/components/button';
import PictureModal from '~/components/pictureModal';
import { ImageZoomRotate } from '~/components/ImageZoomRotate';
import Dialog from '~/components/dialog';
import { BiometriaUnicoDisplay } from '../../models';

interface StateType {
  id: string;
  biometriaType: string;
  cpf: string;
}

interface IUnicoBiometricDetails {
  nomeCompleto: string;
  processId: string;
  resultadoAlertaComportamento: {
    descricao: string;
    nome: string;
  };
  resultadoAutenticacao: {
    descricao: string;
    nome: string;
  };
}

interface ImageProps {
  image: string;
}

interface IErrorResponse {
  mensagem: string;
  status: number;
}

const UnicoBiometricsDetail = () => {
  const [data, setData] = useState<IUnicoBiometricDetails | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [image, setImage] = useState('');
  const [imageLoaded, setImageLoaded] = useState(false);
  const [dialogError, setDialogError] = useState(false);
  const [messageError, setMessageError] = useState('');

  const { state } = useLocation<StateType>();
  const history = useHistory();

  const RequestUnicoBiometricsDetails = useApi<IUnicoBiometricDetails>(
    Endpoints.integration.getUnicoBiometricsDetail(state?.id),
    ApiMethod.GET,
  );

  const RequestUnicoBiometricImage = useApi<ImageProps>(
    Endpoints.integration.getImageUnicoBiometric(state?.cpf, data?.processId),
    ApiMethod.GET,
  );

  const getUnicoBiometricsDetails = async () => {
    if (!state?.id) {
      setDialogError(true);
      setMessageError('ID não encontrado');
      return;
    }

    try {
      const response = await RequestUnicoBiometricsDetails.callApi();
      if (response.status === 200) {
        setData(response?.data);
      } else if (response.status >= 400) {
        setDialogError(true);
        setMessageError(
          (response.data as any)?.mensagem
            ? (response.data as any).mensagem
            : 'Erro ao consultar os detalhes da Unico',
        );
      }
    } catch (error) {
      console.log(error);
      setDialogError(true);
      setMessageError('Erro ao consultar os detalhes da Unico');
    }
  };

  const getUnicoBiometricImage = async () => {
    try {
      const response = await RequestUnicoBiometricImage.callApi();
      const { image } = response.data;
      setImage(image);
      setImageLoaded(true);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (openDialog && !imageLoaded) {
      getUnicoBiometricImage();
    }
  }, [openDialog, imageLoaded]);

  useEffect(() => {
    let isMounted = true;

    if (state?.id) {
      getUnicoBiometricsDetails();
    }

    return () => {
      isMounted = false;
    };
  }, [state?.id, state?.cpf]);

  const handleTogglePicture = () => setOpenDialog((prev) => !prev);

  const goBack = () => {
    history.push('/consult/user/biometrics', {
      fromDetail: true,
    });
  }

  return (
    <>
      {openDialog && (
        <PictureModal handleClose={handleTogglePicture}>
          <div
            style={{
              position: 'relative',
              overflow: 'hidden',
              background: '#fff',
              height: '100%',
            }}
          >
            <ImageZoomRotate
              image={
                image ? image : icons.userAvatar
              }
            />
          </div>
        </PictureModal>
      )}

      <Loading
        status={
          RequestUnicoBiometricsDetails?.loading ||
          RequestUnicoBiometricImage?.loading
        }
      />
      <Dialog
        icon={icons.exclamation}
        open={dialogError}
        positiveLabel="Entendi"
        positiveAction={() => {
          setDialogError(false);
          history.push('/consult/user/biometrics');
        }}
        message={messageError}
      />
      <Container>
        <St.BoxCard>
          <ContainerFlex padding={30}>
            <ContainerFlex row center marginBottom={60}>
              <St.Icon src={icons.exclamation} />
              <St.TitleHeader>
                <span style={{ color: '#e71037' }}>
                  Detalhes biometria facial
                </span>
              </St.TitleHeader>
            </ContainerFlex>

            {data && data.nomeCompleto ? (
              <Grid xs={1} sm={2} lg={2} gap={30}>
                <ContainerFlex>
                  <St.Subtitle>1. Resultado da Autenticação</St.Subtitle>
                  <ContainerFlex marginLeft={20} marginTop={20}>
                    <Input
                      readOnly
                      label="Resultado"
                      value={BiometriaUnicoDisplay[data?.resultadoAutenticacao?.nome] || '-'}
                    />
                  </ContainerFlex>
                  <ContainerFlex marginLeft={20} marginTop={20}>
                    <Input
                      readOnly
                      label="Descrição"
                      value={data.resultadoAutenticacao?.descricao || '-'}
                    />
                  </ContainerFlex>
                  <ContainerFlex marginLeft={20} marginTop={20}>
                    <Input
                      readOnly
                      label="Nome completo"
                      value={data.nomeCompleto}
                    />
                  </ContainerFlex>
                  <ContainerFlex marginLeft={20} marginTop={20}>
                    <Input
                      readOnly
                      label="Process ID"
                      value={data.processId}
                    />
                  </ContainerFlex>
                </ContainerFlex>

                <ContainerFlex>
                  <St.Subtitle>2. Resultado do Alerta de Comportamento</St.Subtitle>
                  <ContainerFlex marginLeft={20} marginTop={20}>
                    <Input
                      readOnly
                      label="Resultado"
                      value={BiometriaUnicoDisplay[data?.resultadoAlertaComportamento?.nome] || '-'}
                    />
                  </ContainerFlex>
                  <ContainerFlex marginLeft={20} marginTop={20}>
                    <Input
                      readOnly
                      label="Descrição"
                      value={data.resultadoAlertaComportamento?.descricao || '-'}
                    />
                  </ContainerFlex>
                  <ContainerFlex marginLeft={20} marginTop={20}>
                  </ContainerFlex>
                  <ContainerFlex marginLeft={20} marginTop={20}>
                  </ContainerFlex>
                </ContainerFlex>
              </Grid>
            ) : (
              <ContainerFlex center>
                <span>Nenhum dado encontrado</span>
              </ContainerFlex>
            )}

            <ContainerFlex row end marginTop={30} gap="30">
              <Button outlined fitContent onClick={goBack}>
                Voltar
              </Button>
              <Button fitContent onClick={() => setOpenDialog(true)}>
                Visualizar imagem
              </Button>
            </ContainerFlex>
          </ContainerFlex>
        </St.BoxCard>
      </Container>
    </>
  );
};

export default UnicoBiometricsDetail;